import { Flex, FormField, Grid, Label, Select, Textarea } from 'amazon-chime-sdk-component-library-react';
import { SelectOptions } from 'amazon-chime-sdk-component-library-react/lib/components/ui/Select';
import { ChangeEvent, FC, useContext, useEffect, useMemo, useState } from 'react';
import PageHeader from '../../components/PageHeader';
import UpButton from '../../components/UPButton';
import { EngagementEventSetting } from '../../constants/http';
import Base from '../../containers/Base';
import useAxios from '../../hooks/useAxios';
import { AppStateContext } from '../../providers/AppStateProvider';
import { useUPProduct } from '../../providers/UPProductProvider/UPProductProvider';
import { IResponse, NotificationActions } from '../../types';
import { IEngagementEventSetting } from '../../types/IEngagementEventSetting';
import { StyledLayout } from './Styled';

type ParsedRow = {
  sourceLineNumber: number;
  sessionDay: number;
  sendTime: string;
  title: string;
  message: string;
};

type ParseResult = {
  validRows: ParsedRow[];
  errors: string[];
};

const preserveUnicodeText = (value: string): string => value.normalize('NFC');

interface CommunicationTracksBulkImportProps {}

const CommunicationTracksBulkImport: FC<CommunicationTracksBulkImportProps> = () => {
  const { fetch: saveSetting, loading: saveSettingLoading } = useAxios<IResponse<string>>();
  const stateContext = useContext(AppStateContext);
  const currentUserInfo = stateContext?.currentUserInfo;
  const { productOptions: contextProductOptions } = useUPProduct();

  const [productOptions, setProductOptions] = useState<SelectOptions[] | undefined>([]);
  const [selectedProductOption, setSelectedProductOption] = useState('');
  const [selectedProductOptionError, setSelectedProductOptionError] = useState<boolean>(false);
  const [bulkContent, setBulkContent] = useState('');
  const [message, setMessage] = useState('');
  const [validationSummary, setValidationSummary] = useState<string[]>([]);

  useEffect(() => {
    if (contextProductOptions) {
      setProductOptions(contextProductOptions);
    }
  }, [contextProductOptions]);

  const canSave = useMemo(() => {
    return selectedProductOption.length > 0 && bulkContent.trim().length > 0;
  }, [bulkContent, selectedProductOption]);

  const productChangedHandler = async (event: ChangeEvent<HTMLSelectElement>) => {
    event.preventDefault();
    event.stopPropagation();
    const productId = event.target.value;
    setSelectedProductOption(productId);
    setSelectedProductOptionError(false);
  };

  const parsePipeDelimitedRows = (rawInput: string): ParseResult => {
    const nonEmptyLines = rawInput
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter((line) => line.length > 0);

    if (nonEmptyLines.length < 3) {
      return {
        validRows: [],
        errors: ['Provide a header row, separator row, and at least one data row.'],
      };
    }

    const extractCells = (line: string): string[] => {
      const trimmedLine = line.trim();
      const withoutLeadingPipe = trimmedLine.startsWith('|') ? trimmedLine.slice(1) : trimmedLine;
      const withoutEdgePipes = withoutLeadingPipe.endsWith('|') ? withoutLeadingPipe.slice(0, -1) : withoutLeadingPipe;
      return withoutEdgePipes.split('|').map((cell) => cell.trim());
    };

    const headerCells = extractCells(nonEmptyLines[0]);
    if (headerCells.length < 6) {
      return {
        validRows: [],
        errors: ['Header must contain at least 6 columns: Session Week, Session Day, Send Time, Title, Message, Notification Type.'],
      };
    }

    const validRows: ParsedRow[] = [];
    const errors: string[] = [];

    nonEmptyLines.slice(2).forEach((line, index) => {
      const cells = extractCells(line);
      const sourceLineNumber = index + 3;

      if (cells.length < 6) {
        errors.push(`Line ${sourceLineNumber}: expected 6 pipe-delimited values.`);
        return;
      }

      const sessionDay = parseInt(cells[1], 10);
      const sendTimeInt = parseInt(cells[2], 10);
      const title = preserveUnicodeText(cells[3]);
      const rowMessage = preserveUnicodeText(cells[4]);

      if (Number.isNaN(sessionDay) || sessionDay < 1) {
        errors.push(`Line ${sourceLineNumber}: Session Day must be a positive integer.`);
        return;
      }

      if (Number.isNaN(sendTimeInt) || sendTimeInt < 1 || sendTimeInt > 24) {
        errors.push(`Line ${sourceLineNumber}: Send Time must be an integer from 1 to 24.`);
        return;
      }

      if (!title) {
        errors.push(`Line ${sourceLineNumber}: Title is required.`);
        return;
      }

      if (!rowMessage) {
        errors.push(`Line ${sourceLineNumber}: Message is required.`);
        return;
      }

      validRows.push({
        sourceLineNumber,
        sessionDay,
        sendTime: sendTimeInt.toString(),
        title,
        message: rowMessage,
      });
    });

    return { validRows, errors };
  };

  const showMessage = (nextMessage: string) => {
    setTimeout(() => setMessage(''), 6000);
    setMessage(nextMessage);
  };

  const onClickSaveHandler = async (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    event.preventDefault();
    event.stopPropagation();

    if (!selectedProductOption) {
      setSelectedProductOptionError(true);
      return;
    }

    const parsed = parsePipeDelimitedRows(bulkContent);
    const failures: string[] = [...parsed.errors];
    let successCount = 0;

    for (const source of parsed.validRows) {
      const engagementEvent: IEngagementEventSetting = {
        uniqueName: undefined,
        communicationType: 'GeneralEngagement',
        locale: 'en',
        subscriptionId: selectedProductOption,
        trackAndDays: selectedProductOption,
        status: 'Active',
        schedule: {
          dayNumber: source.sessionDay,
          timeToSend: source.sendTime,
        },
        sendViaEmail: {
          active: false,
          emailContent: {
            template: '',
          },
        },
        sendViaPush: {
          active: true,
          pushNotificationContent: {
            url: '',
            name: preserveUnicodeText(source.title),
            action: NotificationActions.None,
            contentDescription: preserveUnicodeText(source.message),
            title: preserveUnicodeText(source.title),
          },
        },
      };

      const saveSettingResult = await saveSetting({
        url: EngagementEventSetting.POST,
        method: 'POST',
        data: engagementEvent,
      });

      if (saveSettingResult && saveSettingResult.isSuccess) {
        successCount++;
      } else {
        const apiErrors = saveSettingResult?.errorMessages as Array<{ code: string; message: string }> | null | undefined;
        const apiError = apiErrors ? apiErrors.map((item) => item.message).join(', ') : '';
        failures.push(`Line ${source.sourceLineNumber}: save failed${apiError ? ` - ${apiError}` : ''}.`);
      }
    }

    const summary: string[] = [];
    summary.push(`Parsed ${parsed.validRows.length} valid row(s).`);
    summary.push(`Saved ${successCount} row(s).`);
    if (failures.length > 0) {
      summary.push(`Failed ${failures.length} row(s).`);
    }

    setValidationSummary([...summary, ...failures]);

    if (successCount > 0) {
      showMessage('Bulk import completed. Check validation summary for details.');
    } else {
      showMessage('No rows were saved. Check validation summary for details.');
    }
  };

  return (
    <>
      {currentUserInfo && currentUserInfo.isHealthCoach && (
        <Base>
          <StyledLayout>
            <PageHeader title='Communication Tracks Bulk Import'></PageHeader>
            <Flex
              flexDirection='column'
              layout='fill-space'
              alignItems='initial'
              justifyContent='initial'
              flexWrap='nowrap'
              style={{ marginLeft: 120, marginTop: 18, height: 'auto', width: 800 }}
            >
              <FormField
                field={Select}
                label={'Product'}
                onChange={productChangedHandler}
                value={selectedProductOption}
                options={productOptions}
                fieldProps={{ className: 'up-select' }}
                layout='horizontal'
                errorText='Please select a product'
                error={selectedProductOptionError}
              />

              <FormField
                field={Textarea}
                label={'Pipeline Delimited Content'}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setBulkContent(e.target.value)}
                value={bulkContent}
                layout='horizontal'
                fieldProps={{
                  name: 'pipelineDelimitedContent',
                  placeholder: '| Session Week | Session Day | Send Time | Title | Message | Notification Type |',
                  rows: 14,
                }}
              />

              <Grid gridTemplateColumns='12fr' gridGap='16px' marginTop={12}>
                <UpButton
                  className='submit-button'
                  type='button'
                  style={{ height: 42 }}
                  onClick={(e) => {
                    onClickSaveHandler(e);
                  }}
                  isLoading={saveSettingLoading}
                  disabled={!canSave}
                >
                  <Label>Save</Label>
                </UpButton>
              </Grid>

              <Flex style={{ textAlign: 'left', marginTop: 8 }}>
                <Label style={{ fontStyle: 'italic' }}>{message}</Label>
              </Flex>

              {validationSummary.length > 0 && (
                <Flex flexDirection='column' style={{ marginTop: 12 }}>
                  <Label style={{ fontWeight: 'bold' }}>Validation Summary</Label>
                  {validationSummary.map((line, index) => (
                    <Label key={`${line}-${index}`}>{line}</Label>
                  ))}
                </Flex>
              )}
            </Flex>
          </StyledLayout>
        </Base>
      )}
    </>
  );
};

export default CommunicationTracksBulkImport;
